import React, { useState } from "react";
import { shallowEqual } from "react-redux";
import * as Styles from "./styles/SummaryTaxAndCosts";
import * as ComboListStyles from "../../../../../../common-deprecated/styles/v2/toyota/components/Combolist";
import * as InputGroupStyles from "../../../../../../common-deprecated/styles/v2/toyota/components/InputGroup";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { BnbLabel } from "../../../utils/labels";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import { showPrice } from "../../../../../../common-deprecated/settings/utils/financeSettingUtilsCommon";
import Price from "../common/price/Price";
import { getEcoBonusPromotions } from "../../../../../../common-deprecated/utils/promotionUtils";
import EcoBonusPromotion from "../../../../../../common-deprecated/components/aem/EcoBonusPromotion";
import useFuseFilter from "../../../../../../common-deprecated/hooks/useFuseFilter";
import { setExternalPriceTaxLocation } from "../../../redux/actions/ExternalPriceActions";
import { enableTaxAndCostsSelector, taxLabelsSelector } from "../../../utils/selectorUtils";
import Popover from "../../../../../../common-deprecated/components/popover/aem/Popover";
import { MODAL_FINANCE_DISCLAIMER } from "../../../../../../common-deprecated/utils/modalConstants";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import * as Button from "../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import { IconWrapper } from "../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import SrOnly from "../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

const SummaryTaxAndCosts = (): JSX.Element | null => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [searchActive, setSearchActive] = useState<boolean>(false);
    const [regionsVisible, setRegionsVisible] = useState<boolean>(false);

    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const ecoBonus = useBnbSelector((state) =>
        getEcoBonusPromotions(state.items.promotions, state.car.price.promotions),
    );
    const financeOption = useBnbSelector((state) => state.utils.financeOption);
    const totalPrice = useBnbSelector((state) => state.basket.total);
    const financeInfo = useBnbSelector((state) => state.finance.info);
    const taxLocations = useBnbSelector((state) => state.bnbSettings.taxLocations);
    const currentTaxLocation = useBnbSelector((state) =>
        state.bnbSettings.taxLocations.find((location) => location.id === state.externalPrice.taxLocationId),
    );
    const taxLabels = useBnbSelector(taxLabelsSelector, shallowEqual);
    const enableTaxAndCosts = useBnbSelector(enableTaxAndCostsSelector, shallowEqual);
    // Reuse the Fuse filter initially developed for car-filter brand/model search.
    // If there is ever feedback on some searches not appearing (or too many searches appearing) consider tweaking the treshold.
    const filteredLocationIds = useFuseFilter(searchQuery, taxLocations, 0.25);

    const renderedLocations = searchActive
        ? taxLocations.filter((taxLocation) => filteredLocationIds.includes(taxLocation.id))
        : taxLocations;

    if (!showPrice(commonSettings) || !enableTaxAndCosts || !taxLabels?.length) return null;

    return (
        <>
            <Styles.Title>
                <BnbLabel label="costsAndTaxes" />
            </Styles.Title>
            <Styles.Description>
                <BnbLabel label="costsAndTaxesDescription" />
            </Styles.Description>

            <Styles.TaxLocationWrapper>
                <ComboListStyles.ComboList show>
                    <ComboListStyles.Header onClick={() => setRegionsVisible(!regionsVisible)}>
                        {currentTaxLocation?.label}
                        <Icon variant="chevronDown" />
                    </ComboListStyles.Header>
                    {regionsVisible && (
                        <>
                            <ComboListStyles.Box>
                                <InputGroupStyles.Wrapper isActive>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        placeholder={currentTaxLocation?.label || ""}
                                        onChange={(event) => {
                                            setSearchQuery(event.target.value);
                                            setSearchActive(event.target.value.length > 1);
                                        }}
                                    />
                                    <InputGroupStyles.InputAppend>
                                        <Icon variant="search" />
                                    </InputGroupStyles.InputAppend>
                                </InputGroupStyles.Wrapper>
                            </ComboListStyles.Box>
                            <ComboListStyles.List>
                                {renderedLocations.map((location) => (
                                    <ComboListStyles.ListItem
                                        key={location.id}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            setRegionsVisible(false);
                                            dispatch(setExternalPriceTaxLocation(location.id));
                                        }}
                                    >
                                        {location.label}
                                    </ComboListStyles.ListItem>
                                ))}
                            </ComboListStyles.List>
                        </>
                    )}
                </ComboListStyles.ComboList>
            </Styles.TaxLocationWrapper>

            <Styles.List>
                {!!taxLabels &&
                    taxLabels.map((tax) => (
                        <>
                            <Styles.DescriptionTerm>
                                {tax.label}
                                {tax.disclaimer && <Popover showOnClick content={tax.disclaimer} useHTML />}
                            </Styles.DescriptionTerm>
                            <Styles.DescriptionDetail>
                                {!!tax.prices.length && tax.prices.map((price) => <>{price}</>)}
                            </Styles.DescriptionDetail>
                        </>
                    ))}
            </Styles.List>

            <Styles.Title>
                <BnbLabel label="basketTotal" />
                <div>
                    <EcoBonusPromotion ecoBonus={ecoBonus} />
                </div>
            </Styles.Title>

            <Styles.List>
                <Styles.DescriptionTerm withoutBorder>
                    {financeOption === "cash" ? (
                        <>
                            <BnbLabel label="financeOptionTitleCash" fallbackLabel="financeOptionCash" useHTML />
                        </>
                    ) : (
                        <>
                            {financeInfo.productName ? (
                                <span>financeInfo.productName</span>
                            ) : (
                                <BnbLabel
                                    label="financeOptionTitleMonthly"
                                    fallbackLabel="financeOptionMonthly"
                                    useHTML
                                />
                            )}
                            <Button.Link
                                onClick={(event) => {
                                    event.stopPropagation();
                                    dispatch(
                                        showModal({
                                            type: MODAL_FINANCE_DISCLAIMER,
                                            disclaimer: financeInfo.disclaimer!,
                                            productName: financeInfo.productName || "",
                                        }),
                                    );
                                }}
                            >
                                <IconWrapper>
                                    <Icon variant="info" />
                                </IconWrapper>
                                <SrOnly>
                                    <BnbLabel
                                        label="financeOptionTitleMonthly"
                                        fallbackLabel="financeOptionMonthly"
                                        useHTML
                                    />
                                    <BnbLabel label="bnbA11yButtonMoreInfo" />
                                </SrOnly>
                            </Button.Link>
                        </>
                    )}
                </Styles.DescriptionTerm>
                <Styles.DescriptionDetail hasLargeText withoutBorder>
                    <Price price={totalPrice} financeItemId="total" forceHideMonthlyDisclaimer />
                </Styles.DescriptionDetail>
            </Styles.List>
        </>
    );
};

export default SummaryTaxAndCosts;
